<template>
	<form class="bg-white p-3 w-full" style="box-shadow: 0px 4px 3px 0px rgb(0 0 0 / 0.07);" @submit.prevent="handleSave">
		<div class="mb-2">
			<input
				ref="newCollectionInput"
				v-model="newCollectionName"
				type="text"
				class="input input-sm input-bordered w-full"
				:placeholder="$t('Name')"
				autofocus
			/>
		</div>

		<!-- color picker -->
		<div class="grid grid-cols-9 flex-wrap mb-3 h-6 justify-center items-center">
			<button
				v-for="c in colors"
				:key="c"
				type="button"
				tabindex="-1"
				class="w-4 h-4 self-center justify-self-center rounded-full hover:scale-150 transition-transform outline outline-2 outline-offset-2"
				:style="{ backgroundColor: c, outlineColor: selectedColor === c ? c : 'transparent' }"
				@click="selectColor(c)"
			></button>
			<button
				type="button"
				tabindex="-1"
				class="flex justify-center items-center w-3 h-3 self-center justify-self-center rounded-full hover:scale-[1.75] transition-transform outline outline-2 outline-offset-2"
				:style="{ outlineColor: selectedColor === '' ? '#e2e8f0' : 'transparent', backgroundColor: 'transparent' }"
				@click="selectColor()"
			>
				<XMarkIcon class="w-2.5 h-2.5 text-error-content" />
			</button>
		</div>

		<div class="flex justify-end gap-2">
			<button type="button" class="btn btn-ghost btn-xs" @click="handleCancellation" :disabled="saving">
				{{ $t('Cancel') }}
			</button>

			<button
				type="submit"
				class="btn btn-primary btn-xs"
				:disabled="!newCollectionName.trim() || saving"
			>
				<span v-show="saving" class="loading loading-spinner loading-xs"></span>
				{{ $t('Save') }}
			</button>
		</div>
	</form>
</template>

<script lang="ts">
import { mapStores } from 'pinia';
import { useLessonPlanCollectionsStore } from '@/stores/LessonPlanCollections.store';
import { LessonPlanCollection } from '@/models/LessonPlanCollection.model';
import { XMarkIcon } from '@heroicons/vue/24/solid';
import type { PropType } from 'vue';

export default {
	props: {
		context: {
			type: String,
			default: null,
		},
		// little trick - set this property to the same value as the v-show value in the parent component. By watching this property, we can focus the input whenever the component becomes visible.
		focus: {
			type: Boolean,
			default: true,
		},
		collection: {
			type: Object as PropType<LessonPlanCollection | null>,
			default: null,
		},
	},
	emits: ['requestUnmount', 'created', 'updated'],

	components: {
		XMarkIcon
	},

	data() {
		return {
			newCollectionName: '',
			selectedColor: '',
			saving: false,
			colors: [
				'#3b82f6',
				'#22c55e',
				'#84cc16',
				'#eab308',
				'#f97316',
				'#ef4444',
				'#ec4899',
				'#a855f7'
			]
		};
	},

	computed: {
		...mapStores(useLessonPlanCollectionsStore),

		isEditing(): boolean {
			return !!(this.collection && this.collection.id);
		},
	},

	mounted() {
		if (this.collection) {
			// Pre-fill with existing collection data for editing
			this.newCollectionName = this.collection.name || '';

			this.selectedColor = this.collection.color || '';
		} else {
			this.selectedColor = '';
		}
		if(this.focus) {
			this.setFocusOnInput();
		}
	},

	watch: {
		focus(newVal: boolean) {
			if (newVal === true) {
				// add timeout of 300ms to ensure the input is visible/animation has finished before focusing
				setTimeout(() => {
					this.setFocusOnInput();
				}, 300);
			}
		}
	},

	methods: {
		setFocusOnInput() {
			const el = this.$refs.newCollectionInput as HTMLInputElement | undefined;
			if (el) el.focus();
		},

		selectColor(color: string = '') {
			this.selectedColor = color;
		},

		resetForm() {
			this.newCollectionName = '';
			this.selectedColor = '';
		},

		handleCancellation() {
			this.resetForm();
			this.$emit('requestUnmount');
		},

		async handleSave() {
			if (!this.newCollectionName.trim()) return;

			if (this.isEditing) {
				await this.updateCollection();
			} else {
				await this.createCollection();
			}
		},

		async createCollection() {
			try {
				this.saving = true;
				const collection = await this.lessonPlanCollectionsStore.createCollection(this.newCollectionName, this.selectedColor);

				this.$emit('created', { collection, context: this.context });
				this.resetForm();
				this.$emit('requestUnmount');
			} catch (e: any) {
				console.error('Error creating new collection:', e);
			} finally {
				this.saving = false;
			}
		},

		async updateCollection() {
			try {
				this.saving = true;
				const collection = this.lessonPlanCollectionsStore.getItems().find(
					(l: any) => String(l.id) === String(this.collection?.id)
				);
				if (!collection) {
					console.warn('Collection not found in store, cannot update');
					return;
				}

				collection.name = this.newCollectionName.trim();
				collection.color = this.selectedColor || undefined;

				await this.lessonPlanCollectionsStore.save(collection);

				this.$emit('updated', { collection: collection, context: this.context });
				this.$emit('requestUnmount');
			} catch (e: any) {
				console.error('Error updating collection:', e);
			} finally {
				this.saving = false;
			}
		},
	},
};
</script>
